import {
  AccountInput,
  useDeferredDeepLink,
  useGrippContext,
  useSendAuthCode,
  useStorageKeys,
  useStoredAnonymousIssueIds,
} from '@gripp/shared-logic';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Platform, Pressable, Text, View } from 'react-native';
import { string, object as yupObject } from 'yup';
import { EditProfileForm, FormContext, PhoneValidator } from '../../components';
import { Button } from '../../components/crop-circle/Button';
import { themedStyleSheet } from '../../themes/themedStyleSheet';

type Props = {
  onVerify: () => void;
  onBack: () => void;
};

export const RegisterForm = (props: Props) => {
  const { t } = useTranslation();
  const { storageHandler } = useGrippContext();
  const storageKeys = useStorageKeys();
  const { registerWithAuthCode, isPending } = useSendAuthCode();
  const {
    invitationId,
    // The key `joinSharedAssetView` puts an asset share token under in the
    // OneLink's `deep_link_value`, the way `invitationId` already rides it.
    shareToken,
    anonymousIssueIds: anonymousIssueIdsParam,
    anonymousRoutineIds: anonymousRoutineIdsParam,
  } = useDeferredDeepLink();
  const storedAnonymousIssueIds = useStoredAnonymousIssueIds();
  // Deep-link ids (app-install path) and in-browser ids (web signup) are the
  // same claim; a visitor can only have one of them, but merge rather than
  // prefer so neither path can drop the other's.
  const anonymousIssueIds = Array.from(
    new Set([
      ...(anonymousIssueIdsParam?.split(',').filter(Boolean) ?? []),
      ...storedAnonymousIssueIds,
    ])
  );
  const anonymousRoutineIds = anonymousRoutineIdsParam
    ?.split(',')
    .filter(Boolean);

  const formSchema = yupObject({
    firstName: string().required(),
    lastName: string().optional().nullable(),
    jobTitle: string().optional().nullable(),
    email: string().email().optional().nullable().lowercase(),
    phone: PhoneValidator.optional().nullable(),
    registerType: string().required(),
  }).test('email or phone', (value) => !!(value.phone || value.email));

  const formMethods = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onChange',
    values: {
      firstName: '',
      lastName: '',
      jobTitle: '',
      email: '',
      phone: null,
      registerType: 'email',
    },
  });

  const {
    control,
    handleSubmit,
    formState: { isValid, isDirty },
    setValue,
  } = formMethods;

  useEffect(() => {
    const loadAuthInput = async () => {
      const storedInput = await storageHandler.getItem(
        storageKeys.authCodeInput
      );

      if (storedInput && JSON.parse(storedInput)?.email) {
        setValue('email', JSON.parse(storedInput).email.toLowerCase());
      }
      if (storedInput && JSON.parse(storedInput)?.phone) {
        setValue('phone', JSON.parse(storedInput).phone);
        setValue('registerType', 'phone');
      }
    };

    loadAuthInput();
  }, [setValue, storageHandler, storageKeys.authCodeInput]);

  const onSubmit = async (data: any) => {
    const input = {
      name: {
        first: data.firstName,
        last: data.lastName,
      },
      phone: data.phone,
      email: data.email,
      jobTitle: data.jobTitle,
      registerType: data.registerType,
      // A share token is deliberately NOT sent as an invitation id. With one,
      // `registerWithAuthCode` runs `acceptInvitation`, whose role selection
      // falls through to Workspace Admin; a share recipient must come out
      // Restricted with the one asset the link names, which is what
      // `redeemShareLink` hands out. So the token wins over an invitation id
      // that arrived on the same payload, as it does on web's create-profile
      // page.
      ...(invitationId && !shareToken && { invitationId }),
      ...(anonymousIssueIds?.length && { anonymousIssueIds }),
      ...(anonymousRoutineIds?.length && { anonymousRoutineIds }),
    } as AccountInput;

    if (shareToken) {
      // The account is created here but is signed in only once its auth code
      // is verified, so the link cannot be redeemed yet. Park it as the
      // destination and let it redeem for the signed-in account, exactly as
      // web's `/acceptInvitation` does: `redirectPath` is the key
      // `verifyAuthCode` reads back, and `pendingDestination` is the durable
      // copy — a token arriving deletes `redirectPath`, and a brand-new
      // account is routed through onboarding, which never reads it.
      const sharePath = `/share/${shareToken}`;
      await storageHandler.setItem(storageKeys.redirectPath, sharePath);
      await storageHandler.setItem(storageKeys.pendingDestination, sharePath);
    }

    await registerWithAuthCode(input, props.onVerify);
  };

  return (
    <FormContext.Provider value={formMethods}>
      <View style={styles.container}>
        {Platform.OS !== 'web' && (
          <View style={styles.header}>
            <Text style={styles.headerTitle}>{t('login.aboutYourself')}</Text>
            <Pressable onPress={props.onBack}>
              <Text style={styles.headerSubtitle}>
                {t('login.existingAccount')}
              </Text>
            </Pressable>
          </View>
        )}
        <EditProfileForm control={control} autoFocus />
        <Button
          variant="rectangular"
          size="large"
          state={isDirty && isValid ? 'active' : 'default'}
          disabled={!(isDirty && isValid)}
          loading={isPending}
          onPress={handleSubmit(onSubmit)}
          style={styles.button}
        >
          {t('login.continue')}
        </Button>
      </View>
    </FormContext.Provider>
  );
};

const styles = themedStyleSheet((role) => ({
  container: {
    gap: 24,
    ...(Platform.OS !== 'web' ? { padding: 16 } : {}),
  },
  header: {
    alignSelf: 'stretch',
    gap: 4,
  },
  headerTitle: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 18,
    lineHeight: 24,
    textAlign: 'left',
    color: role.textStrong,
  },
  headerSubtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'left',
    color: role.textStrong,
  },
  button: {
    width: '100%',
  },
}));
